import EnvService from "../../env";
import {matchPath} from "react-router-dom";
import {ApiMethods} from "../../../../@bizkeytech";
import type {Method as AxiosApiMethods} from "axios";


/**
 * This interface houses all the available endpoints of this application specific for the 'biz-print' server.
 */
class ApiEndpoints {

    /**
     * The base url of the api endpoints of this application.
     * @private
     */
    private static baseUrl: string = EnvService.apiEndpointUrl.concat('/api');

    /**
     * The base url of the component state api endpoints of this application.
     * @private
     */
    private static componentStateBaseUrl: string = EnvService.componentStateApiUrl.concat('/api');

    /**
     * The base url of the api endpoints of this application for version 1.
     * @private
     */
    private static v1BaseUrl = this.baseUrl.concat("");

    /**
     * The controllers of the api endpoints for version 1.
     * @private
     */
    private static v1 = {
        bizFile: this.v1BaseUrl.concat("/BizFile"),
        setting: this.v1BaseUrl.concat("/Setting"),
        reportingTools: this.v1BaseUrl.concat("/ReportingTools"),
        uiComponent: this.componentStateBaseUrl.concat("/UIComponent"),
        organizationSettings: this.v1BaseUrl.concat("/OrganizationSettings"),
        organizationGroup: this.v1BaseUrl.concat("/OrganizationGroup"),
        employeeProfiles: this.v1BaseUrl.concat("/EmployeeProfiles"),
        reports: this.v1BaseUrl.concat("/Reports"),
    }

    // ********************************************** V1 Endpoints **********************************************

    /**
     * The endpoints specific for the biz file controller of the server api.
     */
    public static bizFile = {
        baseUrl: this.v1.bizFile,
        uploadRemainingChunk: this.v1.bizFile.concat("/UploadRemainingChunk"),
        createAccessToken: this.v1.bizFile.concat("/CreateAccessToken"),
        uploadFile: this.v1.bizFile.concat("/UploadFile"),
        createStorageAccount: this.v1.bizFile.concat("/CreateStorageAccount"),
        getStorageAccount: this.v1.bizFile.concat("/GetStorageAccount"),
    }

    /**
     * The endpoints specific for the config controller of the server api.
     */
    public static setting = {
        baseUrl: this.v1.setting,
        getPartnerSetting: this.v1.setting.concat('/GetPartnerSettings'),
    }

    /**
     * The endpoints specific for the reporting tools controller of the server api.
     */
    public static reportingTools = {
        baseUrl: this.v1.reportingTools,
        getColumns: this.v1.reportingTools.concat(`/GetColumns`),
    }
    /**
     * The endpoints specific for the data-grid controller of the server api.
     */
    public static uiComponent = {
        baseUrl: this.v1.uiComponent,
        getData: this.v1.uiComponent.concat('/GetApplicationComponents'),
        saveData: this.v1.uiComponent.concat('/Save'),
    }

    /**
     * The endpoints specific for the organization settings controller of the server api.
     */
    public static organizationSettings = {
        baseUrl: this.v1.organizationSettings,
        get: this.v1.organizationSettings,
        create: this.v1.organizationSettings,
        update: this.v1.organizationSettings,
        delete: this.v1.organizationSettings,
        // locations
        createLocation: this.v1.organizationSettings.concat('/Locations'),
        updateLocation: this.v1.organizationSettings.concat('/Locations'),
        deleteLocation: this.v1.organizationSettings.concat('/Locations'),
        regenerateQrCode: this.v1.organizationSettings.concat(`/RegenerateQRCode`),
        // WorkingSchedule
        createWorkingSchedule: this.v1.organizationSettings.concat('/WorkingSchedule'),
        updateWorkingSchedule: this.v1.organizationSettings.concat('/WorkingSchedule'),
        deleteWorkingSchedule: this.v1.organizationSettings.concat('/WorkingSchedule'),
        // WorkPeriods
        createWorkPeriod: this.v1.organizationSettings.concat('/WorkPeriods'),
        updateWorkPeriod: this.v1.organizationSettings.concat('/WorkPeriods'),
        deleteWorkPeriod: this.v1.organizationSettings.concat('/WorkPeriods'),
        // Holidays
        createHoliday: this.v1.organizationSettings.concat('/Holidays'),
        updateHoliday: this.v1.organizationSettings.concat('/Holidays'),
        deleteHoliday: this.v1.organizationSettings.concat('/Holidays'),
        // SpecialWorkSessions
        createSpecialWorkSession: this.baseUrl.concat('/SpecialWorkSession'),
        updateSpecialWorkSession: this.baseUrl.concat('/SpecialWorkSession'),
        deleteSpecialWorkSession: this.baseUrl.concat('/SpecialWorkSession'),
    }

    /**
     * The endpoints specific for the organization group controller of the server api.
     */
    public static organizationGroup = {
        baseUrl: this.v1.organizationGroup,
        list: this.v1.organizationGroup,
        create: this.v1.organizationGroup,
        update: this.v1.organizationGroup,
        delete: this.v1.organizationGroup,
    }

    /**
     * The endpoints specific for the employee profiles controller of the server api.
     */
    public static employeeProfiles = {
        baseUrl: this.v1.employeeProfiles,
        list: this.v1.employeeProfiles.concat('/All'),
        getCurrentProfile: this.v1.employeeProfiles,
        getEmployeeProfile: this.v1.employeeProfiles,
        activateEmployeeProfile: this.v1.employeeProfiles.concat('/Activate'),
        deactivateEmployeeProfile: this.v1.employeeProfiles.concat('/Deactivate'),
        updateEmployeeProfile: this.v1.employeeProfiles,
        searchEmployeeProfiles: this.v1.employeeProfiles.concat('/Search'),
        getEmployeeProfileHistory: this.v1.employeeProfiles.concat('/History'),
        getEmployeeProfilePropertyHistory: this.v1.employeeProfiles.concat('/PropertyHistory'),
    }

    /**
     * The endpoints specific for the reports controller of the server api.
     */
    public static reports = {
        baseUrl: this.v1.reports,
        getPayrollReport: this.v1.reports,
    }


    // ********************************************** Intents **********************************************


    /**
     * The intent descriptions for each API endpoint.
     * @private
     */
    private static apiIntents: { [key: string]: string | { [method: string]: string } } = {
        [this.bizFile.uploadRemainingChunk]: "Upload a remaining chunk of a file.",
        [this.bizFile.createAccessToken]: "Create an access token for a file.",
        [this.bizFile.createStorageAccount]: "Create a storage account for the partner with default capacity value.",
        [this.bizFile.getStorageAccount]: "Get the information of the current storage account.",
        [this.setting.getPartnerSetting]: "Fetch system settings.",
        [this.reportingTools.getColumns]: "Retrieve reporting tools data including columns.",
        [this.uiComponent.getData]: "Retrieve application components data.",
        [this.uiComponent.saveData]: "Save application components data.",
        [this.organizationSettings.create]: {
            [ApiMethods.post]: "Create organization settings.",
            [ApiMethods.put]: "Update organization settings.",
            [ApiMethods.delete]: "Delete organization settings.",
        },
        [this.organizationSettings.createLocation]: {
            [ApiMethods.post]: "Create location.",
            [ApiMethods.put]: "Update location.",
            [ApiMethods.delete]: "Delete location.",
        },
        [this.organizationSettings.regenerateQrCode]: "Regenerate QR code.",
        [this.organizationSettings.createWorkingSchedule]: {
            [ApiMethods.post]: "Create working schedule.",
            [ApiMethods.put]: "Update working schedule.",
            [ApiMethods.delete]: "Delete working schedule.",
        },
        [this.organizationSettings.createWorkPeriod]: {
            [ApiMethods.post]: "Create work period.",
            [ApiMethods.put]: "Update work period.",
            [ApiMethods.delete]: "Delete work period.",
        },
        [this.organizationSettings.createHoliday]: {
            [ApiMethods.post]: "Create holiday.",
            [ApiMethods.put]: "Update holiday.",
            [ApiMethods.delete]: "Delete holiday.",
        },
        [this.organizationSettings.createSpecialWorkSession]: {
            [ApiMethods.post]: "Create special work session.",
            [ApiMethods.put]: "Update special work session.",
            [ApiMethods.delete]: "Delete special work session.",
        },
        [this.organizationGroup.create]: {
            [ApiMethods.get]: "List organization groups.",
            [ApiMethods.post]: "Create organization group.",
            [ApiMethods.put]: "Update organization group.",
            [ApiMethods.delete]: "Delete organization group.",
        },
        [this.employeeProfiles.list]: "List employee profiles.",
        [this.employeeProfiles.getCurrentProfile]: "Fetch current employee profile.",
        [this.reports.getPayrollReport]: "Fetch payroll report.",
    };

    /**
     * Get the intent description for a given API endpoint and method.
     *
     * @param endpoint      the API endpoint.
     * @param method        the HTTP method.
     * @returns             the intent description.
     */
    public static getIntent(endpoint: string, method?: AxiosApiMethods): string | undefined {
        const normalize = (str: string) => str
            .replace(this.baseUrl, '')
            .replace(/\?.*$/, '')
        const normalizedEndpoint = normalize(endpoint);
        const endpoints = Object.keys(this.apiIntents);
        const matchedEndpoints = endpoints
            .map(path => matchPath({path: normalize(path), end: false, caseSensitive: false}, normalizedEndpoint))
            .map((e, index) => {
                if (!e) return e;
                return endpoints[index];
            })
            .filter(match => !!match)
        if (matchedEndpoints.length <= 0) {
            return undefined;
        }

        let matchedEndpoint;
        if (matchedEndpoints.length > 1) {
            matchedEndpoint = matchedEndpoints.find(e => e === endpoint) ?? matchedEndpoints[0];
        } else {
            matchedEndpoint = matchedEndpoints[0]
        }

        const intent = this.apiIntents[matchedEndpoint!];
        if (typeof intent === 'string') {
            return intent;
        }
        return intent?.[method ?? ""];
    }
}

export default ApiEndpoints;
